import FInputError from '../../../components/BaseComponents/FInputError'
import SInputError from '../../../components/opinionless/SInputErrorV2'
import SInput from '../../../components/opinionless/SInputV2'

import SText from '../../../components/opinionless/SText'

import { useRouter } from 'next/router'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import Select from 'react-select'
import SBox from '../../../components/opinionless/SBox'
import SFlex from '../../../components/opinionless/SFlex'
import SInputLabel from '../../../components/opinionless/SInputLabelV2'
import { s18n, useTranslation } from '../../../i18n'
import { PhoneNumberStyles } from '../../../styles/phoneNumber'
import sSelectStyle from '../../../styles/select'
import STheme, { SStyled, useSTheme } from '../../../styles/theme'
import { useAddApplicationAnswersMutation, useApplyMutation } from '../../../types/graphql'

import { formattedDateYYYYMMDD, isOfAge } from 'utils/date'

import AddressLBInputGroup from 'components/AddressLBInputGroup'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { desktopThreshold } from 'components/BaseComponents/constants'
import STextWithTooltip from 'components/opinionless/STextWithTooltip'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import ReactDatePicker from 'react-datepicker'
import MaskedInput from 'react-text-mask'
import { ga4_useTitle } from 'utils/ga4'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { states as statesList } from '../../../utils/states'
import { OrganizationContext } from '../../../v2/context/organization'
import { useApplicationAnswers } from '../../../v2/hooks/useApplicationAnswers'
import { LBButton } from './ApplicationStepLBIntroduction'
import { LBInputLabel, SRadioLabelV2, SRadioV2 } from './ApplicationStepLBRemainingQuestions'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import { useIntercom } from '../../../v2/hooks/useIntercom'

interface Props {
    setCurrentStep?: (number) => void
    previousStep: any
    onSuccess: (string) => void
    fontFamily: any
    updatePercentage: any
}

const InputRow = SStyled(SFlex)`
    flex-direction: row;
    margin-top: 24px;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            flex-direction: column;
        }
    `}
`

const SBoxNameContainer = SStyled(SBox)`
    flex-direction: row;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            flex-direction: column;
        }
    `}
`

const SBoxLastName = SStyled(SBox)`
    margin-left: 8px;
    margin-top: 3px;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-left: 0px;
            margin-top: 8px;
        }
    `}
`

const LBTextHeader = SStyled(SText)`
    ${(p) => `

        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 24px;
            line-height: 130%;
        }
    `}
`
export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

export const languagesSelectValue = () => {
    return window.LANGUAGES.map((language) => {
        return {
            label: language.text,
            value: language.code,
        }
    })
}

export const getSelectedLanguageValue = (langCode) => {
    return langCode
        ? {
              value: window.LANGUAGES.find((i) => i.code === langCode).code,
              label: window.LANGUAGES.find((i) => i.code === langCode).text,
          }
        : null
}

const LBTextSubHeader = SStyled(SText)`
    margin-top: 24px;

    ${(p) => `

        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 14px;
            line-height: 130%;
            color: #202020;
            opacity: 87%;
        }
    `}
`

const ApplicationStepLBBasicInfo: React.FC<Props> = (props: Props) => {
    ga4_useTitle('Basic info')
    const { t } = useTranslation(window.LANG_NS)
    const applicationContext = useContext(ApplicationContext)
    const { refetch } = useApplicationBasicInfo()

    const requiredString = 'required_field'
    const theme = useSTheme()

    const form = useForm({
        defaultValues: {
            addressLine1: applicationContext.location?.addressLine1,
            addressLine2: applicationContext.location?.addressLine2,
            city: applicationContext.location?.city,
            state: applicationContext.location?.state
                ? {
                      value: applicationContext.location?.state,
                      label: statesList[applicationContext.location?.state],
                  }
                : null,
            language: getSelectedLanguageValue(applicationContext.profile?.language),
            postalCode: applicationContext.location?.zipCode,
        },
    })

    form.watch('language')

    const { getAnswersByCategory } = useApplicationAnswers()
    const { initIntercom } = useIntercom()

    const organizationContext = useContext(OrganizationContext)
    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [gender, setGender] = useState(applicationContext.profile.gender)
    const [housingStatusRadioBtn, setHousingStatusRadioBtn] = useState(null)
    const [blindOrDisabledRadioBtn, setBlindOrDisabledRadioBtn] = useState(null)
    const [addApplicationAnswers] = useAddApplicationAnswersMutation()
    const [isMobile, setIsMobile] = useState<boolean>(false)
    const router = useRouter()
    const fontFamily = props.fontFamily

    const [programApply, { loading }] = useApplyMutation()

    useEffect(() => {
        if (applicationContext.application.id) {
            router.replace(
                `${organizationContext.program.urlFragment}/apply/${applicationContext.application.id}/basic`
            )
        }
    }, [])

    useEffect(() => {
        const homelessAnswers = getAnswersByCategory('HOMELESS_STATUS')
        if (homelessAnswers[0]?.response === 'True') {
            setHousingStatusRadioBtn('housing_status_answer_no_housing')
            form.setValue('housingStatus', 'housing_status_answer_no_housing')
        }

        const housingAnswers = getAnswersByCategory('HOUSING_STATUS')
        if (housingAnswers[0]) {
            setHousingStatusRadioBtn(housingAnswers[0].response)
            form.setValue('housingStatus', housingAnswers[0].response)
        }
    }, [applicationContext.answers])

    useEffect(() => {
        if (applicationContext.profile && applicationContext.profile.blindDisabled !== undefined) {
            setBlindOrDisabledRadioBtn(applicationContext.profile.blindDisabled ? 'yes' : 'no')
        }
        form.setValue('blindDisabled', applicationContext.profile.blindDisabled ? 'yes' : 'no')
        form.setValue('dob', applicationContext.profile.dob)
        form.setValue(
            'ethnicity',
            lbEthnicityOptions.find((i) => i.id === applicationContext.profile.ethnicity)
        )
    }, [applicationContext.profile])

    useEffect(() => {
        form.setValue('phoneNumber', applicationContext.communication?.phoneNumber)
    }, [applicationContext.communication])

    useEffect(() => {
        if (successMessage) {
            setTimeout(() => {
                setSuccessMessage('')
            }, 3000)
        }

        if (errorMessage) {
            setTimeout(() => {
                setErrorMessage('')
            }, 3000)
        }
    }, [successMessage, errorMessage])

    useEffect(() => {
        form.register(
            {
                name: 'dob',
                type: 'custom',
            },
            {
                required: 'required_field' as string,
                validate: isOfAge,
            }
        )
        return () => {
            form.unregister('dob')
        }
    }, [])

    const languageSelected = (value) => {
        void s18n.changeLanguage(value.value)
    }

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const onResize = () => {
        setIsMobile(window.innerWidth < desktopThreshold)
    }

    useEffect(() => {
        onResize()
        window.removeEventListener('resize', onResize)
        window.addEventListener('resize', onResize)
    }, [])

    useEffect(() => {
        if (form.getValues('language')) {
            if (s18n.language) {
                form.setValue('language', getSelectedLanguageValue(s18n.language))
            }
        }
    }, [s18n.language, t, form, form.setValue])

    useEffect(() => {
        if (!form.getValues('language' && applicationContext.profile?.language)) {
            const selectedLanguageOption = getSelectedLanguageValue(applicationContext.profile?.language)
            form.setValue('language', selectedLanguageOption)
        }
    }, [applicationContext.profile?.language, form.setValue])

    const lbEthnicityValues = [
        'ethnicity_option_asian',
        'ethnicity_option_black_african_american',
        'ethnicity_option_white',
        'ethnicity_option_hispanic_latino',
        'ethnicity_option_middle_eastern_north_african',
        'ethnicity_option_indian_alaska',
        'ethnicity_option_multiracial',
        'ethnicity_option_other',
        'ethnicity_option_prefer_not_state',
    ]
    const lbEthnicityOptions = useMemo(() => {
        return lbEthnicityValues.map((ethnicityValue) => ({
            id: ethnicityValue,
            label: t(ethnicityValue),
        }))
    }, [t])

    const onFormSubmit = (values) => {
        const variables = {
            email: values.email,
            phoneNumber: values.phoneNumber,
            dob: values.dob,
            firstName: values?.firstName,
            lastName: values?.lastName,
            addressLine1: values?.addressLine1 || '',
            addressLine2: values?.addressLine2?.length > 0 ? values.addressLine2 : null,
            country: 'US',
            city: values?.city || '',
            language: values.language.value,
            state: values?.state?.value || '',
            postalCode: values?.postalCode || '',
            programUrl: router.query['program-name'] as string,
            gender: gender,
            isHomeless: housingStatusRadioBtn === 'housing_status_answer_no_housing',
            blindDisabled: values?.blindDisabled === 'yes' ? true : false,
            ethnicity: values.ethnicity.id,
            applicationId: applicationContext.application?.id,
        }

        programApply({
            variables,
        })
            .then((response) => {
                if (response.data.apply.application) {
                    addApplicationAnswers({
                        variables: {
                            answers: [
                                {
                                    personApplicationId: response.data.apply.application.id,
                                    category: 'HOUSING_STATUS',
                                    response: values.housingStatus,
                                },
                            ],
                        },
                    }).then((res) => {
                        if (res.data.addApplicationAnswers.success === true) {
                            refetch()

                            const personId = atob(response.data.apply.application.id).split(':')[1]
                            const intercomKey = response.data.apply.application.intercomKey
                            initIntercom(
                                personId,
                                intercomKey,
                                values.email,
                                values.firstName,
                                values.lastName,
                                values.phoneNumber
                            )
                            setTimeout(() => {
                                props.onSuccess(response.data.apply.application.id)
                                router.replace(
                                    `${organizationContext.program.urlFragment}/apply/${response.data.apply.application.id}/basic`
                                )
                            }, 500)
                        } else {
                            setErrorMessage(t('error'))
                        }
                    })
                } else {
                    if (response.data.apply.error) {
                        setErrorMessage(t('error_already_signed_up'))
                    } else {
                        setErrorMessage(t('error'))
                    }
                }
            })
            .catch(() => setErrorMessage(t('error')))
    }

    const handleHousingStatus = (e) => {
        form.setValue('housingStatus', e.target.getAttribute('value'))
        form.clearErrors('housingStatus')
        setHousingStatusRadioBtn(e.target.getAttribute('value'))
    }

    const handleBlindDisabledStatus = (e) => {
        form.setValue('blindDisabled', e.target.getAttribute('value'))
        form.clearErrors('blindDisabled')
        setBlindOrDisabledRadioBtn(e.target.getAttribute('value'))
    }

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.basicInfo,
        WIZARD_STEPS_MAPPING.basicInfo.applicantInfo,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    return (
        <Container>
            <FormProvider {...form}>
                <SBox
                    as="form"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    m={0}
                    onSubmit={form.handleSubmit(onFormSubmit)}
                >
                    <FButtonPrevious onClick={props.previousStep} />
                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.secondaryColor,
                            lineHeight: '120%',
                            fontFamily: fontFamily.header,
                            fontWeight: '900',
                        }}
                    >
                        {t('applicant_information')}
                    </LBTextHeader>
                    <LBTextSubHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '16px',
                            marginBottom: '10px',
                            color: theme.colors.black,
                            fontFamily: 'Raleway',
                        }}
                        dangerouslySetInnerHTML={{ __html: t('tell_about_yourself') }}
                    ></LBTextSubHeader>
                    <InputRow justifyContent={'space-between'}>
                        <SBox sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <SInputLabel
                                style={{
                                    fontFamily: fontFamily.body,
                                    color: theme.colors.blackLabelfont,
                                }}
                            >
                                {t('first_name_input_placeholder')}
                            </SInputLabel>

                            <SBoxNameContainer sx={{ display: 'flex' }}>
                                <SBox flex={0.5} mt={'3px'}>
                                    <SInput
                                        id="firstName"
                                        name="firstName"
                                        placeholder={t('first_name_input_placeholder')}
                                        defaultValue={applicationContext.profile.firstName}
                                        ref={form.register({
                                            required: requiredString,
                                            minLength: {
                                                value: 2,
                                                message: 'error_name_at_least_two_characters',
                                            },
                                            pattern: {
                                                value: /^(?=.{2,25}$)[a-zA-ZÀ-ú-( )]+(?: [a-zA-ZÀ-ú-( )]+)?$/,
                                                message: 'error_special_characters_not_allowed',
                                            },
                                        })}
                                        sx={{
                                            fontFamily: fontFamily.body,
                                            color: theme.colors.blackLabelfont,
                                            borderColor: form.errors.firstName ? theme.colors.redError : 'inputBorder',
                                            '::placeholder': {
                                                fontFamily: fontFamily.body,
                                                color: theme.colors.grayLabelPlaceholder,
                                            },
                                            '&:hover': {
                                                borderColor: form.errors.firstName
                                                    ? theme.colors.redError
                                                    : 'inputBorder',
                                            },
                                        }}
                                    />
                                    {form.errors.firstName && (
                                        <FInputError id={'firstNameError'} forElementId={'firstName'}>
                                            {t(form.errors.firstName?.message)}
                                        </FInputError>
                                    )}
                                </SBox>
                                <SBoxLastName flex={0.5}>
                                    <SInput
                                        id="lastName"
                                        name="lastName"
                                        placeholder={t('last_name_input_placeholder')}
                                        defaultValue={applicationContext.profile.lastName}
                                        ref={form.register({
                                            required: requiredString,
                                            minLength: {
                                                value: 2,
                                                message: 'error_name_at_least_two_characters',
                                            },
                                            pattern: {
                                                value: /^([a-zA-ZÀ-ú-( )]+\s)*[a-zA-ZÀ-ú-( )]+$/,
                                                message: 'error_special_characters_not_allowed',
                                            },
                                        })}
                                        sx={{
                                            fontFamily: fontFamily.body,
                                            color: theme.colors.blackLabelfont,
                                            borderColor: form.errors.lastName ? theme.colors.redError : 'inputBorder',
                                            '::placeholder': {
                                                fontFamily: fontFamily.body,
                                                color: theme.colors.grayLabelPlaceholder,
                                            },
                                            '&:hover': {
                                                borderColor: form.errors.lastName
                                                    ? theme.colors.redError
                                                    : 'inputBorder',
                                            },
                                            marginTop: isMobile && form.errors.firstName ? '20px' : '0px',
                                        }}
                                    />
                                </SBoxLastName>
                            </SBoxNameContainer>
                        </SBox>
                    </InputRow>
                    {form.errors.lastName && (
                        <SInputError
                            sx={{
                                fontFamily: fontFamily.body,
                                color: theme.colors.redError,
                            }}
                            forElementId={'lastName'}
                        >
                            {t(form.errors.lastName?.message)}
                        </SInputError>
                    )}
                    <LBInputLabel
                        sx={{
                            color: STheme.colors.textColor,
                            fontFamily: fontFamily.body,
                            fontSize: '14px',
                            fontWeight: '700',
                            marginBottom: '0px !important',
                        }}
                    >
                        {t('housing_status_question')}
                    </LBInputLabel>
                    <InputRow
                        id="housing_status"
                        sx={{
                            display: 'flex !important',
                            flexDirection: 'column !important',
                            marginTop: '12px !important',
                        }}
                    >
                        <SRadioLabelV2
                            color={theme.colors.textColor}
                            sx={{
                                fontFamily: fontFamily.body,
                            }}
                            marginBottom={'5px'}
                        >
                            <SBox maxWidth={'35px'} marginRight={'11px'}>
                                <SRadioV2
                                    name="housing_status_answer_stable"
                                    id="housing_status_answer_stable"
                                    type="radio"
                                    value="housing_status_answer_stable"
                                    checked={housingStatusRadioBtn === 'housing_status_answer_stable'}
                                    color={
                                        housingStatusRadioBtn === 'housing_status_answer_stable'
                                            ? theme.colors.secondaryColor
                                            : STheme.colors.grayUnselectedRadioOutline
                                    }
                                    onClick={handleHousingStatus}
                                    {...form.register('housingStatus', {
                                        required: 'required_selection' as string,
                                    })}
                                    size={'26px'}
                                />
                            </SBox>

                            <SText
                                sx={{
                                    width: '100%',
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    lineHeight: '140%',
                                    fontWeight: '400',
                                    fontSize: '14px',
                                    color: theme.colors.gray400,
                                    fontFamily: fontFamily.body,
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('housing_status_answer_stable_v2'),
                                }}
                            />
                        </SRadioLabelV2>
                        <SRadioLabelV2
                            color={theme.colors.textColor}
                            sx={{
                                fontFamily: fontFamily.body,
                            }}
                        >
                            <SBox maxWidth={'35px'} marginRight={'11px'}>
                                <SRadioV2
                                    name="housing_status_answer_unstable"
                                    id="housing_status_answer_unstable"
                                    type="radio"
                                    value="housing_status_answer_unstable"
                                    color={
                                        housingStatusRadioBtn === 'housing_status_answer_unstable'
                                            ? theme.colors.secondaryColor
                                            : STheme.colors.grayUnselectedRadioOutline
                                    }
                                    checked={housingStatusRadioBtn === 'housing_status_answer_unstable'}
                                    onClick={handleHousingStatus}
                                    {...form.register('housingStatus', {
                                        required: 'required_selection' as string,
                                    })}
                                    size={'26px'}
                                />
                            </SBox>

                            <SText
                                sx={{
                                    width: '100%',
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    lineHeight: '140%',
                                    fontWeight: '400',
                                    fontSize: '14px',
                                    color: theme.colors.gray400,
                                    fontFamily: fontFamily.body,
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('housing_status_answer_unstable_v2'),
                                }}
                            />
                        </SRadioLabelV2>
                        <SRadioLabelV2
                            color={theme.colors.textColor}
                            sx={{
                                fontFamily: fontFamily.body,
                            }}
                        >
                            <SBox maxWidth={'35px'} marginRight={'11px'}>
                                <SRadioV2
                                    name="housing_status_answer_no_housing"
                                    id="housing_status_answer_no_housing"
                                    type="radio"
                                    value="housing_status_answer_no_housing"
                                    color={
                                        housingStatusRadioBtn === 'housing_status_answer_no_housing'
                                            ? theme.colors.secondaryColor
                                            : STheme.colors.grayUnselectedRadioOutline
                                    }
                                    checked={housingStatusRadioBtn === 'housing_status_answer_no_housing'}
                                    onClick={handleHousingStatus}
                                    {...form.register('housingStatus', {
                                        required: 'required_selection' as string,
                                    })}
                                    size={'26px'}
                                />
                            </SBox>

                            <SText
                                sx={{
                                    width: '100%',
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    lineHeight: '140%',
                                    fontWeight: '400',
                                    fontSize: '14px',
                                    color: theme.colors.gray400,
                                    fontFamily: fontFamily.body,
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('homeless_or_unhoused'),
                                }}
                            />
                        </SRadioLabelV2>
                    </InputRow>
                    <SInputError
                        forElementId={'housing_status'}
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                    >
                        {t(form.errors.housingStatus?.message)}
                    </SInputError>
                    <SInputError
                        forElementId={'blind_disabled'}
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                    >
                        {t(form.errors.blindDisabled?.message)}
                    </SInputError>
                    <AddressLBInputGroup fontFamily={fontFamily.body} />
                    <InputRow>
                        <SBox flex={1}>
                            <SBox
                                sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'left' }}
                            >
                                <STextWithTooltip
                                    text={t('phone_number_label')}
                                    tooltip={t('phone_number_tooltip_text')}
                                    textStyle={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.blackLabelfont,
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        lineHeight: '140%',
                                        fontWeight: '700',
                                        fontSize: '14px',
                                        opacity: '87%',
                                    }}
                                />
                            </SBox>
                            <Controller
                                control={form.control}
                                name="phoneNumber"
                                rules={{
                                    required: 'required_field' as string,
                                    pattern: {
                                        value: /^\+1\d{10}$/,
                                        message: 'INVALID_PHONE_NUMBER',
                                    },
                                }}
                                height={'50px'}
                                defaultValue={applicationContext.communication.phoneNumber}
                                render={({ onChange, onBlur, value }) => {
                                    return (
                                        <SBox sx={{ position: 'relative' }} id={'phoneNumber-field-wrapper'}>
                                            <PhoneNumberStyles.PlusOne
                                                style={{
                                                    fontFamily: fontFamily.body,
                                                    minHeight: '50px',
                                                    top: '15px',
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    lineHeight: '140%',
                                                    opacity: '60%',
                                                    fontSize: '14px',
                                                    color: form.getValues('phoneNumber')
                                                        ? theme.colors.blackLabelfont
                                                        : theme.colors.grayLabelPlaceholder,
                                                }}
                                            >
                                                +1
                                            </PhoneNumberStyles.PlusOne>

                                            <PhoneNumberStyles.StyledPhoneInput
                                                id={'phoneNumber'}
                                                international={true}
                                                placeholder={t('phone_number_input_placeholder')}
                                                country={'US'}
                                                maxLength={12}
                                                value={value}
                                                onChange={onChange}
                                                style={{
                                                    fontFamily: fontFamily.body,
                                                    minHeight: '50px',
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    lineHeight: '140%',
                                                    fontSize: '14px',
                                                    borderRadius: '8px',
                                                    color: theme.colors.blackLabelfont,
                                                    borderColor: form.errors.phoneNumber
                                                        ? theme.colors.redError
                                                        : theme.colors.inputBorder,
                                                    '::placeholder': {
                                                        fontFamily: fontFamily.body,
                                                        color: theme.colors.grayLabelPlaceholder,
                                                    },
                                                }}
                                            />
                                        </SBox>
                                    )
                                }}
                            />
                            {form.errors.phoneNumber && (
                                <SInputError
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.redError,
                                    }}
                                    forElementId={'phoneNumber-field-wrapper'}
                                >
                                    {t(form.errors.phoneNumber?.message)}
                                </SInputError>
                            )}
                        </SBox>
                    </InputRow>
                    <InputRow>
                        <SBox flex={1}>
                            <SBox
                                sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'left' }}
                            >
                                <STextWithTooltip
                                    text={t('email_label')}
                                    tooltip={t('email_tooltip_text')}
                                    textStyle={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.blackLabelfont,
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        lineHeight: '140%',
                                        fontWeight: '700',
                                        fontSize: '14px',
                                        opacity: '87%',
                                    }}
                                />
                            </SBox>
                            <SInput
                                name="email"
                                id="email"
                                placeholder={t('email_input_placeholder')}
                                ref={form.register({
                                    required: requiredString,
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                        message: 'INVALID_EMAIL_ADDRESS',
                                    },
                                })}
                                defaultValue={applicationContext.communication.email}
                                sx={{
                                    fontFamily: fontFamily.body,
                                    color: theme.colors.blackLabelfont,
                                    borderColor: form.errors.email ? theme.colors.redError : 'inputBorder',
                                    '::placeholder': {
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.grayLabelPlaceholder,
                                    },
                                    '&:hover': {
                                        borderColor: form.errors.email ? theme.colors.redError : 'inputBorder',
                                    },
                                }}
                            />
                            {form.errors.email && (
                                <SInputError
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.redError,
                                    }}
                                    forElementId={'email'}
                                >
                                    {t(form.errors.email?.message)}
                                </SInputError>
                            )}
                        </SBox>
                    </InputRow>
                    <InputRow>
                        <SBox flex={1} id={'dob-wrapper'}>
                            <SBox
                                sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'left' }}
                            >
                                <SInputLabel
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.blackLabelfont,
                                    }}
                                >
                                    {t('application_dob_label')}
                                </SInputLabel>
                            </SBox>
                            <Controller
                                control={form.control}
                                name="dob"
                                defaultValue={applicationContext.profile.dob || null}
                                render={({ onChange, value }) => {
                                    return (
                                        <ReactDatePicker
                                            onSelect={(itm) => onChange(itm)}
                                            selected={value}
                                            placeholderText={t('dob_new_placeholder')}
                                            onChange={(itm) => onChange(itm)} //
                                            customInput={
                                                <MaskedInput
                                                    mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                                    render={(ref, props) => (
                                                        <SInput
                                                            name="dob"
                                                            id="dob"
                                                            sx={{
                                                                fontFamily: fontFamily.body,
                                                                color: theme.colors.blackLabelfont,
                                                                borderRadius: '8px',
                                                                borderColor: form.errors.dob
                                                                    ? theme.colors.redError
                                                                    : 'inputBorder',
                                                                '::placeholder': {
                                                                    fontFamily: fontFamily.body,
                                                                    color: theme.colors.grayLabelPlaceholder,
                                                                },
                                                                '&:hover': {
                                                                    borderColor: form.errors.dob
                                                                        ? theme.colors.redError
                                                                        : 'inputBorder',
                                                                },
                                                            }}
                                                            ref={ref}
                                                            {...props}
                                                        />
                                                    )}
                                                />
                                            }
                                        />
                                    )
                                }}
                            />
                            {form.errors.dob && (
                                <SInputError
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.redError,
                                    }}
                                    forElementId={'dob-wrapper'}
                                >
                                    {t(form.errors.dob?.message)}
                                </SInputError>
                            )}
                        </SBox>
                    </InputRow>
                    <InputRow>
                        <SBox flex={1}>
                            <SBox
                                sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'left' }}
                            >
                                <SInputLabel
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.blackLabelfont,
                                    }}
                                >
                                    {t('language_label')}
                                </SInputLabel>
                            </SBox>
                            <Controller
                                name="language"
                                control={form.control}
                                render={({ onChange, value }) => {
                                    return (
                                        <Select
                                            name="language"
                                            id="language-field"
                                            {...form.register('language', {
                                                required: requiredString,
                                            })}
                                            options={languagesSelectValue()}
                                            placeholder={t('language_input_placeholder')}
                                            components={{ IndicatorSeparator: () => null }}
                                            onChange={(option: any) => {
                                                onChange(option)
                                                languageSelected(option)
                                            }}
                                            value={form.getValues('language')}
                                            theme={(theme) => ({
                                                ...theme,
                                                colors: {
                                                    ...theme.colors,
                                                    primary: theme.colors.inputBorder,
                                                    neutral30: theme.colors.inputBorder,
                                                },
                                            })}
                                            styles={{
                                                ...sSelectStyle(theme),
                                                container: (provided, state) => ({
                                                    ...sSelectStyle(theme).container(provided, state),
                                                    flex: 2,
                                                }),
                                                control: (provided, state) => ({
                                                    ...sSelectStyle(theme).control(provided, state),
                                                    fontFamily: fontFamily.body,
                                                    borderRadius: '8px',
                                                    borderColor: form.errors.ethnicity
                                                        ? theme.colors.redError
                                                        : theme.colors.inputBorder,
                                                }),
                                                option: (provided, state) => ({
                                                    ...sSelectStyle(theme).option(provided, state),
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    lineHeight: '140%',
                                                    fontWeight: '400',
                                                    fontSize: '14px',
                                                    fontFamily: fontFamily.body,
                                                    color: theme.colors.blackLabelfont,
                                                    opacity: '80%',
                                                }),
                                                placeholder: (provided, state) => ({
                                                    ...sSelectStyle(theme).option(provided, state),
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    lineHeight: '140%',
                                                    fontWeight: '400',
                                                    fontSize: '14px',
                                                    color: theme.colors.grayLabelPlaceholder,
                                                }),
                                            }}
                                        />
                                    )
                                }}
                            />

                            {form.errors.language && (
                                <SInputError
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.redError,
                                    }}
                                    forElementId={'language-field'}
                                >
                                    {t(form.errors.language?.message)}
                                </SInputError>
                            )}
                        </SBox>
                    </InputRow>
                    <InputRow justifyContent={'space-between'}>
                        <SBox flex={1}>
                            <SBox sx={{ display: 'flex !important', flexDirection: 'row !important' }}>
                                <STextWithTooltip
                                    text={t('ethnicity_field_label')}
                                    tooltip={t('ethnicity_tooltip_text')}
                                    textStyle={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.blackLabelfont,
                                        width: 'auto',
                                        marginRight: '3px',
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        lineHeight: '140%',
                                        fontWeight: '700',
                                        fontSize: '14px',
                                        opacity: '87%',
                                    }}
                                />
                            </SBox>
                            <Controller
                                name="ethnicity"
                                control={form.control}
                                defaultValue={
                                    lbEthnicityOptions.find((i) => i.id === applicationContext.profile.ethnicity) ||
                                    null
                                }
                                rules={{ required: requiredString }}
                                render={(props: any) => {
                                    const { onChange, value } = props
                                    return (
                                        <Select
                                            name="ethnicity"
                                            id="ethnicity"
                                            mb={10}
                                            options={lbEthnicityOptions}
                                            placeholder={t('ethnicity_field_placeholder')}
                                            components={{ IndicatorSeparator: () => null }}
                                            onChange={(option: any) => {
                                                onChange(option)
                                            }}
                                            value={value}
                                            theme={(theme) => ({
                                                ...theme,
                                                colors: {
                                                    ...theme.colors,
                                                    primary: theme.colors.inputBorder,
                                                    neutral30: theme.colors.inputBorder,
                                                },
                                            })}
                                            styles={{
                                                ...sSelectStyle(theme),
                                                container: (provided, state) => ({
                                                    ...sSelectStyle(theme).container(provided, state),
                                                    flex: 2,
                                                }),
                                                control: (provided, state) => ({
                                                    ...sSelectStyle(theme).control(provided, state),
                                                    fontFamily: fontFamily.body,
                                                    borderRadius: '8px',
                                                    borderColor: form.errors.ethnicity
                                                        ? theme.colors.redError
                                                        : theme.colors.inputBorder,
                                                }),
                                                option: (provided, state) => ({
                                                    ...sSelectStyle(theme).option(provided, state),
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    lineHeight: '140%',
                                                    fontWeight: '400',
                                                    fontSize: '14px',
                                                    fontFamily: fontFamily.body,
                                                    color: theme.colors.blackLabelfont,
                                                    opacity: '80%',
                                                }),
                                                placeholder: (provided, state) => ({
                                                    ...sSelectStyle(theme).option(provided, state),
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    lineHeight: '140%',
                                                    fontWeight: '400',
                                                    fontSize: '14px',
                                                    color: theme.colors.grayLabelPlaceholder,
                                                }),
                                            }}
                                            maxMenuHeight={500}
                                        />
                                    )
                                }}
                            />
                            {form.errors.ethnicity && (
                                <SInputError
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.redError,
                                    }}
                                    forElementId={'ethnicity'}
                                >
                                    {t(form.errors.ethnicity?.message)}
                                </SInputError>
                            )}
                        </SBox>
                    </InputRow>
                    <LBInputLabel
                        sx={{
                            color: STheme.colors.textColor,
                            fontFamily: fontFamily.body,
                            fontSize: '14px',
                            fontWeight: '700',
                            marginBottom: '0px !important',
                        }}
                    >
                        {t('blind_or_disabled_question')}
                    </LBInputLabel>

                    <InputRow
                        id="blind_disabled"
                        sx={{
                            display: 'flex !important',
                            flexDirection: 'column !important',
                            marginTop: '12px !important',
                        }}
                    >
                        <SRadioLabelV2
                            color={theme.colors.textColor}
                            sx={{
                                fontFamily: fontFamily.body,
                            }}
                            marginBottom={'8px'}
                        >
                            <SBox maxWidth={'35px'} marginRight={'11px'}>
                                <SRadioV2
                                    name="blind_disabled_answer_yes"
                                    id="blind_disabled_answer_yes"
                                    type="radio"
                                    value="yes"
                                    checked={blindOrDisabledRadioBtn === 'yes'}
                                    color={
                                        blindOrDisabledRadioBtn === 'yes'
                                            ? theme.colors.secondaryColor
                                            : STheme.colors.grayUnselectedRadioOutline
                                    }
                                    onClick={handleBlindDisabledStatus}
                                    {...form.register('blindDisabled', {
                                        required: 'required_selection' as string,
                                    })}
                                    size={'26px'}
                                />
                            </SBox>

                            <SText
                                sx={{
                                    width: '100%',
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    lineHeight: '140%',
                                    fontWeight: '400',
                                    fontSize: '14px',
                                    color: theme.colors.gray400,
                                    fontFamily: fontFamily.body,
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('yes'),
                                }}
                            />
                        </SRadioLabelV2>
                        <SRadioLabelV2
                            color={theme.colors.textColor}
                            sx={{
                                fontFamily: fontFamily.body,
                            }}
                        >
                            <SBox maxWidth={'35px'} marginRight={'11px'}>
                                <SRadioV2
                                    name="blind_disabled_answer_no"
                                    id="blind_disabled_answer_no"
                                    type="radio"
                                    value="no"
                                    color={
                                        blindOrDisabledRadioBtn === 'no'
                                            ? theme.colors.secondaryColor
                                            : STheme.colors.grayUnselectedRadioOutline
                                    }
                                    checked={blindOrDisabledRadioBtn === 'no'}
                                    onClick={handleBlindDisabledStatus}
                                    {...form.register('blindDisabled', {
                                        required: 'required_selection' as string,
                                    })}
                                    size={'26px'}
                                />
                            </SBox>

                            <SText
                                sx={{
                                    width: '100%',
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    lineHeight: '140%',
                                    fontWeight: '400',
                                    fontSize: '14px',
                                    color: theme.colors.gray400,
                                    fontFamily: fontFamily.body,
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('no'),
                                }}
                            />
                        </SRadioLabelV2>
                    </InputRow>
                    <SText
                        sx={{
                            textAlignLast: 'left',
                            fontWeight: '400',
                            fontSize: '10px',
                            marginTop: '16px',
                            fontFamily: fontFamily.body,
                            color: theme.colors.gray400,
                        }}
                    >
                        {t('basic_info_disclaimer')}
                    </SText>

                    {successMessage && (
                        <SText color={theme.colors.secondaryColor} mt={3} mb={7} fontSize={3}>
                            {successMessage}
                        </SText>
                    )}
                    {errorMessage && (
                        <SText
                            sx={{
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                fontWeight: '500',
                                fontSize: '10px',
                                fontFamily: fontFamily.body,
                                color: theme.colors.redError,
                            }}
                            mt={3}
                            mb={2}
                            fontSize={3}
                        >
                            {errorMessage}
                        </SText>
                    )}
                    <LBButton
                        type="submit"
                        sx={['bg']}
                        title={t('save_and_continue')}
                        width={'100%'}
                        loading={loading}
                        textStyle={{
                            fontWeight: 700,
                            fontSize: '14px',
                            lineHeight: '140%',
                            fontFamily: fontFamily.body,
                            color: theme.colors.white,
                        }}
                    >
                        {t('save_and_continue')}
                    </LBButton>
                </SBox>
            </FormProvider>
        </Container>
    )
}

export default ApplicationStepLBBasicInfo
